import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
  Shield, Plus, Edit2, Trash2, CheckCircle, XCircle, Search, 
  AlertCircle, Calendar, CheckSquare, Square, Table as TableIcon, Grid as GridIcon, Info, Layers 
} from 'lucide-react';
import UIElementGuard from './UIElementGuard';
import './RoleManagement.css';

function RoleManagement({ onRolesUpdated, onAddCategoriesClick }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [searchTerm, setSearchTerm] = useState('');
  const [permSearchTerm, setPermSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    permission_ids: [] 
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/university/roles'),
        api.get('/university/permissions')
      ]);
      setRoles(rolesRes.data || []);
      setPermissions(permsRes.data || []);
      if (typeof onRolesUpdated === 'function') {
        onRolesUpdated();
      }
    } catch (error) {
      console.error("Failed to fetch roles/permissions", error);
      setErrorMessage("Failed to load roles and permissions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    setErrorMessage('');
    setPermSearchTerm('');
    if (role) {
      setEditingRole(role);
      setFormData({ 
        name: role.name || role.role_name || '', 
        description: role.description || '', 
        permission_ids: role.permissions ? role.permissions.map(p => p.id) : [] 
      });
    } else {
      setEditingRole(null);
      setFormData({ name: '', description: '', permission_ids: [] });
    }
    setShowModal(true);
  };

  const handleTogglePermission = (id) => {
    setFormData(prev => {
      if (prev.permission_ids.includes(id)) {
        return { ...prev, permission_ids: prev.permission_ids.filter(p => p !== id) };
      }
      return { ...prev, permission_ids: [...prev.permission_ids, id] };
    });
  };

  const handleSelectAllPermissions = () => {
    const filteredIds = filteredPermissions.map(p => p.id);
    const uniqueIds = Array.from(new Set([...formData.permission_ids, ...filteredIds]));
    setFormData(prev => ({ ...prev, permission_ids: uniqueIds }));
  };

  const handleClearPermissions = () => {
    const filteredIds = filteredPermissions.map(p => p.id);
    setFormData(prev => ({ 
      ...prev, 
      permission_ids: prev.permission_ids.filter(id => !filteredIds.includes(id)) 
    }));
  };

  const formatErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) {
      return err.map(item => formatErrorMessage(item)).filter(Boolean).join('; ');
    }
    if (typeof err === 'object') {
      if (err.msg) return err.msg;
      if (err.detail) return formatErrorMessage(err.detail);
      if (err.message) return formatErrorMessage(err.message);
      try {
        return JSON.stringify(err);
      } catch (e) {
        return 'An error occurred.';
      }
    }
    return String(err);
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setErrorMessage("Role Name is required.");
      return;
    }
    if (!formData.permission_ids || formData.permission_ids.length === 0) {
      setErrorMessage("Please select at least one permission for this role.");
      return;
    }
    try {
      setIsSaving(true);
      setErrorMessage('');
      const payload = {
        name: formData.name.trim(),
        role_name: formData.name.trim(),
        description: formData.description.trim(),
        permission_ids: formData.permission_ids
      };

      if (editingRole) {
        await api.put(`/university/roles/${editingRole.id}`, payload);
        showToast("Role updated successfully!");
      } else {
        await api.post('/university/roles', payload);
        showToast("Role created successfully!");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save role", error);
      const msg = formatErrorMessage(error.response?.data?.detail) || "An error occurred while saving the role.";
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      setErrorMessage('');
      await api.delete(`/university/roles/${roleId}`);
      showToast("Role deleted successfully!");
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      console.error("Failed to delete role", error);
      const msg = formatErrorMessage(error.response?.data?.detail) || "Failed to delete role.";
      setErrorMessage(msg);
    }
  };

  const showToast = (message) => {
    setSuccessMessage(formatErrorMessage(message));
    setTimeout(() => {
      setSuccessMessage('');
    }, 4000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const filteredRoles = roles.filter(role => {
    const nameMatch = (role.name || role.role_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const descMatch = (role.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return nameMatch || descMatch;
  });

  const filteredPermissions = permissions.filter(perm => {
    const nameMatch = (perm.name || '').replace(/_/g, ' ').toLowerCase().includes(permSearchTerm.toLowerCase());
    const descMatch = (perm.description || '').toLowerCase().includes(permSearchTerm.toLowerCase());
    return nameMatch || descMatch;
  });

  const categorizePermissions = (perms) => {
    const categories = {
      Students: [],
      Faculty: [],
      TPO: [],
      Companies: [],
      'Jobs / Placements': [],
      Other: []
    };
    
    perms.forEach(perm => {
      const name = perm.name.toLowerCase();
      if (name.includes('student')) {
        categories.Students.push(perm);
      } else if (name.includes('faculty') || name.includes('courses_faculty')) {
        categories.Faculty.push(perm);
      } else if (name.includes('tpo')) {
        categories.TPO.push(perm);
      } else if (name.includes('compan')) {
        categories.Companies.push(perm);
      } else if (name.includes('placement_drives') || name.includes('drive') || name.includes('applied_candidates') || name.includes('unapprove_job')) {
        categories['Jobs / Placements'].push(perm);
      } else {
        categories.Other.push(perm);
      }
    });
    
    return Object.entries(categories).filter(([_, items]) => items.length > 0);
  };

  const groupedPermissions = categorizePermissions(filteredPermissions);

  if (loading) {
    return (
      <div className="role-loading-state">
        <div className="spinner"></div>
        <p>Loading Dynamic Roles & Permissions...</p>
      </div>
    );
  }

  return (
    <div className="role-management-container">
      {successMessage && (
        <div className="alert-toast success-toast">
          <CheckCircle size={18} />
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && !showModal && (
        <div className="alert-toast error-toast">
          <AlertCircle size={18} />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="role-header">
        <div className="role-header-title">
          <div className="header-icon-wrapper">
            <Shield size={24} className="accent-icon" />
          </div>
          <div>
            <h2>Dynamic Role Management</h2>
            <p className="role-subtitle">Create custom roles and configure granular permissions for faculty and TPO members.</p>
          </div>
        </div>

        <div className="role-header-actions">
          <div className="view-mode-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <TableIcon size={16} /> Table
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid Cards View"
            >
              <GridIcon size={16} /> Cards
            </button>
          </div>

          <div style={{ display: 'flex', gap: '15px' }}>
            <UIElementGuard page="role_management" element="create_role_btn">
              <button className="primary-btn pulse-glow" onClick={() => handleOpenModal()}>
                <Plus size={18} /> Create Custom Role
              </button>
            </UIElementGuard>
          </div>
        </div>
      </div>

      <div className="role-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            placeholder="Search roles by name or description..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>
        <div className="roles-counter">
          <span>Total Roles: <strong>{filteredRoles.length}</strong></span>
        </div>
      </div>

      {filteredRoles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Shield size={48} />
          </div>
          <h3>No Roles Found</h3>
          <p>
            {searchTerm 
              ? "No roles match your search criteria. Try adjusting your search keywords."
              : "No custom roles created yet. Create a role to assign specific permissions to your faculty and TPOs."}
          </p>
          {!searchTerm && (
            <button className="primary-btn mt-4" onClick={() => handleOpenModal()}>
              <Plus size={16} /> Create Your First Role
            </button>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="roles-table-container">
          <table className="roles-table">
            <thead>
              <tr>
                <th>Role Details</th>
                <th>Description</th>
                <th>Permissions Count & Tags</th>
                <th>Created At</th>
                <th>Last Updated</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.map(role => {
                const permList = role.permissions || [];
                const permCount = role.permission_count !== undefined ? role.permission_count : permList.length;
                
                return (
                  <tr key={role.id} className="role-table-row">
                    <td>
                      <div className="role-name-cell">
                        <div className="role-shield-icon">
                          <Shield size={18} />
                        </div>
                        <span className="role-name-text">{role.name || role.role_name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="role-desc-text">
                        {role.description ? role.description : <span className="text-muted italic">No description provided</span>}
                      </span>
                    </td>
                    <td>
                      <div className="permissions-cell">
                        <span className="permission-count-badge">
                          {permCount} {permCount === 1 ? 'Permission' : 'Permissions'}
                        </span>
                        <div className="permission-tags-preview">
                          {permList.slice(0, 3).map(p => (
                            <span key={p.id} className="perm-chip">
                              {p.name.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {permList.length > 3 && (
                            <span className="perm-chip more-chip">+{permList.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="date-cell">
                      <div className="date-info">
                        <Calendar size={14} />
                        <span>{formatDate(role.created_at)}</span>
                      </div>
                    </td>
                    <td className="date-cell">
                      <div className="date-info">
                        <Calendar size={14} />
                        <span>{formatDate(role.updated_at)}</span>
                      </div>
                    </td>
                    <td className="actions-cell text-right">
                      {deleteConfirmId === role.id ? (
                        <div className="delete-confirm-actions">
                          <span className="confirm-text">Delete?</span>
                          <button 
                            className="btn-confirm-yes" 
                            onClick={() => handleDeleteRole(role.id)}
                            title="Confirm Delete"
                          >
                            Yes
                          </button>
                          <button 
                            className="btn-confirm-no" 
                            onClick={() => setDeleteConfirmId(null)}
                            title="Cancel"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="role-action-buttons">
                          <button 
                            className="action-icon-btn edit-btn" 
                            onClick={() => handleOpenModal(role)}
                            title="Edit Role"
                          >
                            <Edit2 size={16} />
                          </button>
                          <UIElementGuard page="role_management" element="delete_role_btn">
                            <button 
                              className="action-icon-btn delete-btn" 
                              onClick={() => setDeleteConfirmId(role.id)}
                              title="Delete Role"
                            >
                              <Trash2 size={16} />
                            </button>
                          </UIElementGuard>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="roles-grid">
          {filteredRoles.map(role => {
            const permList = role.permissions || [];
            const permCount = role.permission_count !== undefined ? role.permission_count : permList.length;

            return (
              <div key={role.id} className="role-card pop-fade-up">
                <div className="role-card-header">
                  <div className="role-card-title">
                    <h3><Shield size={20} className="card-shield" /> {role.name || role.role_name}</h3>
                    {role.description && <p className="role-card-desc">{role.description}</p>}
                  </div>
                  <div className="role-card-actions">
                    <button className="icon-btn edit-icon" onClick={() => handleOpenModal(role)} title="Edit Role">
                      <Edit2 size={16} />
                    </button>
                    {deleteConfirmId === role.id ? (
                      <div className="card-delete-confirm">
                        <button className="btn-confirm-yes" onClick={() => handleDeleteRole(role.id)}>Yes</button>
                        <button className="btn-confirm-no" onClick={() => setDeleteConfirmId(null)}>No</button>
                      </div>
                    ) : (
                      <UIElementGuard page="role_management" element="delete_role_btn">
                        <button className="icon-btn delete-icon" onClick={() => setDeleteConfirmId(role.id)} title="Delete Role">
                          <Trash2 size={16} />
                        </button>
                      </UIElementGuard>
                    )}
                  </div>
                </div>

                <div className="role-card-meta">
                  <span className="permission-count-badge">
                    {permCount} {permCount === 1 ? 'Permission Assigned' : 'Permissions Assigned'}
                  </span>
                  {role.updated_at && (
                    <span className="meta-date">Updated: {formatDate(role.updated_at)}</span>
                  )}
                </div>

                <div className="role-permissions-list">
                  {permissions.map(perm => {
                    const hasPerm = permList.some(p => p.id === perm.id);
                    return (
                      <div key={perm.id} className={`permission-item ${hasPerm ? 'active' : 'inactive'}`}>
                        {hasPerm ? <CheckCircle size={15} className="text-green" /> : <XCircle size={15} className="text-red" />}
                        <div className="perm-item-text">
                          <span className="perm-item-name">{perm.name.replace(/_/g, ' ')}</span>
                          {perm.description && <span className="perm-item-sub">{perm.description}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content role-modal pop-fade-up">
            <div className="modal-header">
              <div className="modal-title-wrapper">
                <Shield size={22} className="accent-icon" />
                <h3>{editingRole ? 'Edit Custom Role' : 'Create Custom Role'}</h3>
              </div>
              <button className="close-modal-btn" onClick={() => setShowModal(false)}>×</button>
            </div>

            {errorMessage && (
              <div className="modal-error-banner">
                <AlertCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSaveRole}>
              <div className="form-group">
                <label htmlFor="role-name">Role Name <span className="req-star">*</span></label>
                <input 
                  id="role-name"
                  type="text" 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Head of Department, Placement Coordinator, Exam Controller"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="role-desc">Description <span className="optional-label">(Optional)</span></label>
                <textarea 
                  id="role-desc"
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter a brief summary of what this role is responsible for..."
                  rows={2}
                />
              </div>
              
              <div className="permissions-checklist">
                <div className="checklist-header">
                  <div>
                    <h4>Assign Permissions <span className="req-star">*</span> ({formData.permission_ids.length} of {permissions.length} selected)</h4>
                    <p className="subtitle">Select the specific privileges users assigned to this role will receive.</p>
                  </div>
                  <div className="checklist-quick-actions">
                    <button type="button" className="quick-select-btn" onClick={handleSelectAllPermissions}>
                      <CheckSquare size={14} /> Select All
                    </button>
                    <button type="button" className="quick-select-btn" onClick={handleClearPermissions}>
                      <Square size={14} /> Clear All
                    </button>
                  </div>
                </div>

                <div className="permissions-search">
                  <Search size={16} />
                  <input 
                    type="text"
                    placeholder="Filter permissions..."
                    value={permSearchTerm}
                    onChange={(e) => setPermSearchTerm(e.target.value)}
                  />
                  {permSearchTerm && (
                    <button type="button" className="clear-perm-search" onClick={() => setPermSearchTerm('')}>×</button>
                  )}
                </div>

                <div className="grouped-checkboxes-container">
                  {groupedPermissions.length === 0 ? (
                    <div className="no-perms-found">No permissions matching "{permSearchTerm}"</div>
                  ) : (
                    groupedPermissions.map(([category, perms]) => (
                      <div key={category} className="permission-category-group" style={{ marginBottom: '20px' }}>
                        <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{category}</h5>
                        <div className="checkbox-grid">
                          {perms.map(perm => {
                            const isChecked = formData.permission_ids.includes(perm.id);
                            return (
                              <label key={perm.id} className={`checkbox-label ${isChecked ? 'checked' : ''}`}>
                                <input 
                                  type="checkbox" 
                                  checked={isChecked}
                                  onChange={() => handleTogglePermission(perm.id)}
                                />
                                <div className="checkbox-text">
                                  <span className="perm-name">{perm.name.replace(/_/g, ' ')}</span>
                                  {perm.description && <span className="perm-desc">{perm.description}</span>}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={isSaving}>
                  {isSaving ? (
                    <span className="btn-saving-text"><span className="spinner-small"></span> Saving Role...</span>
                  ) : (
                    editingRole ? 'Update Role' : 'Create Role'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleManagement;
